/**
 * Scenario View Component
 * Main view for playing through a scenario
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ScenarioView.css';
import ChatInterface from '../Chat/ChatInterface';
import Whiteboard from '../Whiteboard/Whiteboard';
import AIMentor from '../Mentor/AIMentor';
import ScenarioNudges from './ScenarioNudges';
import ArtifactRequirementsPanel from './ArtifactRequirementsPanel';
import { FileText, MessageCircle, X, Info, Lock, ListTodo, Upload, Award } from 'lucide-react';
import { SCENARIO_DATA } from '../../data/scenarios';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { canAccessScenario } from '../../utils/scenarioSequencing';
import { checkTaskCompletionCriteria } from '../../utils/taskCompletion';
import { getConversationHistory, getAllUploads, saveScenarioUpload } from '../../utils/api';
import ScenarioComplete from './ScenarioComplete';

const ScenarioView = () => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { progress, updateTaskCompletion, addCheckpoint, updateProgress } = useProgress();
  const [scenario, setScenario] = useState(null);
  const [whiteboardOpen, setWhiteboardOpen] = useState(false);
  const [mentorOpen, setMentorOpen] = useState(false);
  const [mentorInitialMessage, setMentorInitialMessage] = useState(null);
  const [currentTask, setCurrentTask] = useState(0);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [conversationHistory, setConversationHistory] = useState({});
  const [scenarioComplete, setScenarioComplete] = useState(false);
  const [sequencingCheck, setSequencingCheck] = useState(null);
  const [activeOverlay, setActiveOverlay] = useState(null); // 'context', 'uploads', 'tasks', or null
  const [completeScenarioMessage, setCompleteScenarioMessage] = useState(null); // validation message when user clicks Complete Scenario
  const [uploadError, setUploadError] = useState(null); // when save to artifacts fails
  const hasCheckedSequencing = useRef(false);

  // When navigating to a different scenario (e.g. "Continue to Next Scenario"), close the completion overlay and re-check access
  useEffect(() => {
    setScenarioComplete(false);
    hasCheckedSequencing.current = false; // Re-run sequencing check for the new scenario
  }, [scenarioId]);

  // Check scenario sequencing on mount and whenever scenarioId or progress changes
  useEffect(() => {
    if (!scenarioId || !progress || hasCheckedSequencing.current) return;
    
    const scenarioData = SCENARIO_DATA[scenarioId];
    if (!scenarioData) return;

    // Block AI track scenarios
    if (scenarioData.track === 'AI') {
      setSequencingCheck({ accessible: false, reason: 'AI Track coming soon' });
      hasCheckedSequencing.current = true;
      return;
    }

    // Check if scenario is accessible (uses completedScenarios from progress)
    const accessCheck = canAccessScenario(scenarioId, progress.completedScenarios || []);
    setSequencingCheck(accessCheck);
    hasCheckedSequencing.current = true;

    if (accessCheck.accessible) {
      setScenario(scenarioData);
    }
  }, [scenarioId, progress]);

  // Fetch all saved uploads from Supabase when on a scenario page and authenticated - single source of truth from database (survives refresh)
  useEffect(() => {
    if (!isAuthenticated || !scenarioId) return;
    let cancelled = false;

    const applyUploads = (uploads) => {
      if (cancelled) return;
      const list = (uploads || []).map(u => ({
        id: u.id || `saved-${u.scenarioId || ''}-${u.fileName}-${u.uploadedAt}`,
        scenarioId: u.scenarioId ?? u.scenario_id ?? null,
        fileName: u.fileName || u.file_name,
        taskIndex: u.taskIndex ?? u.task_index ?? null,
        fileSize: u.fileSize ?? u.file_size ?? null,
        uploadedAt: u.uploadedAt || u.uploaded_at
      }));
      setArtifacts(list.filter(a => a.scenarioId));
    };

    const fetch = () =>
      getAllUploads()
        .then(applyUploads)
        .catch((err) => {
          if (cancelled) return;
          const is401 = err?.response?.status === 401;
          if (is401) {
            // Session may still be restoring after refresh; retry once after a short delay
            setTimeout(() => {
              if (cancelled) return;
              getAllUploads().then(applyUploads).catch((e) => {
                if (!cancelled) console.warn('[ScenarioView] Could not load uploads (after retry):', e?.message || e);
              });
            }, 600);
          } else {
            console.warn('[ScenarioView] Could not load uploads from database:', err?.message || err);
          }
        });

    fetch();
    return () => { cancelled = true; };
  }, [isAuthenticated, scenarioId]);

  // Restore saved uploads from progress when returning to a completed scenario (backup if API has none yet)
  useEffect(() => {
    if (!scenarioId || !progress?.completedScenarios) return;
    const completedEntry = progress.completedScenarios.find(s => (s.id || s) === scenarioId);
    const savedUploads = completedEntry?.uploads;
    if (!savedUploads?.length) return;
    setArtifacts(prev => {
      const forThisScenario = prev.filter(a => a.scenarioId === scenarioId);
      if (forThisScenario.length > 0) return prev;
      const restored = savedUploads.map((u, i) => ({
        id: `saved-${scenarioId}-${i}-${u.fileName || ''}`,
        scenarioId,
        fileName: u.fileName || 'Uploaded document',
        taskIndex: u.taskIndex ?? null,
        fileSize: u.fileSize ?? null,
        uploadedAt: completedEntry.completedAt || new Date().toISOString()
      }));
      return [...prev.filter(a => a.scenarioId !== scenarioId), ...restored];
    });
  }, [scenarioId, progress?.completedScenarios]);

  // Load completed tasks and artifacts
  useEffect(() => {
    if (!scenarioId || !progress) return;

    const taskCompletions = progress.taskCompletions || {};
    const completed = taskCompletions[scenarioId] || [];
    
    // Filter out tasks that shouldn't be completed (those without required artifacts)
    // Only keep tasks that have requiredArtifact and were legitimately completed
    const scenarioData = SCENARIO_DATA[scenarioId];
    if (scenarioData) {
      const validCompleted = completed.filter(taskIndex => {
        const task = scenarioData.tasks[taskIndex];
        return task && task.requiredArtifact; // Only tasks with required artifacts can be completed
      });
      setCompletedTasks(validCompleted);
      
      // If we filtered out some tasks, update the progress
      if (validCompleted.length !== completed.length && progress) {
        const updatedCompletions = {
          ...taskCompletions,
          [scenarioId]: validCompleted
        };
        // Update progress to remove invalid completions
        updateProgress({ ...progress, taskCompletions: updatedCompletions });
      }
    } else {
      setCompletedTasks(completed);
    }

    // Load conversation history to check task completion criteria
    const loadConversations = async () => {
      const channels = ['engineering', 'design', 'data', 'leadership', 'customer-success'];
      const history = {};
      
      for (const channel of channels) {
        try {
          const { messages } = await getConversationHistory(scenarioId, channel);
          if (messages && messages.length > 0) {
            history[channel] = messages;
          }
        } catch (error) {
          console.error(`Error loading conversation for ${channel}:`, error);
        }
      }
      
      setConversationHistory(history);
    };

    loadConversations();
  }, [scenarioId, progress, updateProgress]);

  // Check task completion criteria periodically
  useEffect(() => {
    if (!scenario || !scenarioId) return;

    const checkTasks = async () => {
      for (let i = 0; i < scenario.tasks.length; i++) {
        if (completedTasks.includes(i)) continue;

        const criteriaMet = checkTaskCompletionCriteria(
          i,
          scenarioId,
          scenario.tasks[i],
          conversationHistory,
          artifacts
        );

        if (criteriaMet) {
          // Mark task as complete
          await updateTaskCompletion(scenarioId, i);
          setCompletedTasks(prev => [...prev, i].sort((a, b) => a - b));

          // Log checkpoint
          const checkpoint = {
            scenarioId,
            taskIndex: i,
            taskTitle: scenario.tasks[i].title,
            completedAt: new Date().toISOString(),
            artifacts: artifacts.filter(a => a.scenarioId === scenarioId && (a.taskIndex === i || a.taskIndex === null)).map(a => a.fileName),
            stakeholders: Object.keys(conversationHistory)
          };
          await addCheckpoint(checkpoint);
        }
      }

      // Scenario completion is now only via "Complete Scenario" pill button (no auto-complete)
    };

    const interval = setInterval(checkTasks, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [scenario, scenarioId, conversationHistory, artifacts, completedTasks, progress, updateTaskCompletion, addCheckpoint]);

  // Show loading while checking sequencing
  if (sequencingCheck === null && !scenario) {
    return (
      <div className="scenario-loading">
        <p>Loading scenario...</p>
      </div>
    );
  }

  // Show locked message if scenario is not accessible
  if (sequencingCheck && !sequencingCheck.accessible) {
    return (
      <div className="scenario-loading">
        <div className="coming-soon-message">
          <Lock size={48} style={{ marginBottom: '16px', color: 'var(--text-secondary)' }} />
          <h2>Scenario Locked</h2>
          <p>{sequencingCheck.reason || 'This scenario is not yet available.'}</p>
          {sequencingCheck.missingScenarios && sequencingCheck.missingScenarios.length > 0 && (
            <div style={{ marginTop: '16px', marginBottom: '24px' }}>
              <p style={{ fontWeight: 600, marginBottom: '8px' }}>Complete these scenarios first:</p>
              <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                {sequencingCheck.missingScenarios.map(id => {
                  const s = SCENARIO_DATA[id];
                  return <li key={id}>{s ? s.title : id}</li>;
                })}
              </ul>
            </div>
          )}
          <button 
            onClick={() => navigate('/app')}
            className="back-button"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Check if scenario is blocked (AI track)
  const scenarioData = SCENARIO_DATA[scenarioId];
  if (scenarioData?.track === 'AI') {
    return (
      <div className="scenario-loading">
        <div className="coming-soon-message">
          <h2>Coming Soon</h2>
          <p>The AI Track will be available in a future update.</p>
          <button 
            onClick={() => window.history.back()}
            className="back-button"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!scenario) {
    return (
      <div className="scenario-loading">
        <p>Loading scenario...</p>
      </div>
    );
  }

  const scenarioContext = {
    scenarioId: scenario.id,
    title: scenario.title,
    currentTask: currentTask,
    tasks: scenario.tasks,
    currentSituation: scenario.currentSituation,
    company: scenario.company,
    currentTaskTitle: scenario.tasks[currentTask]?.title,
    currentTaskDescription: scenario.tasks[currentTask]?.description,
    description: scenario.description,
    timeline: scenario.timeline,
    constraints: scenario.constraints
  };

  // Get upload count for pill button
  // Scenario 1 (Discovery & Definition) requires exactly one upload: Product Brief (PDF)
  const isDiscoveryScenario = scenarioId === 'saas-1';
  const requiredTasks = isDiscoveryScenario
    ? scenario.tasks.filter(t => t.requiredArtifact).slice(-1) // only the single required deliverable
    : scenario.tasks.filter(t => t.requiredArtifact);
  const uploadedCount = requiredTasks.filter(task => {
    return artifacts.some(artifact => {
      if (artifact.scenarioId !== scenarioId) return false;
      const fileName = artifact.fileName.toLowerCase();
      const requiredType = task.requiredArtifact.toLowerCase();
      const requiredKeywords = requiredType.split(' ').filter(k => k.length > 2);
      return requiredKeywords.some(keyword => fileName.includes(keyword)) ||
             fileName.split('.')[0].includes(requiredType.split(' ')[0]);
    });
  }).length;
  const totalRequired = requiredTasks.length;
  const uploadLabel = totalRequired === 1 ? 'Required Upload' : 'Required Uploads';
  const canCompleteScenario = totalRequired === 0 || uploadedCount >= totalRequired;

  const handleCompleteScenarioClick = () => {
    setCompleteScenarioMessage(null);
    if (canCompleteScenario) {
      setScenarioComplete(true);
    } else {
      setCompleteScenarioMessage(
        totalRequired === 1
          ? 'Upload the required document (e.g. Product Brief PDF) in the Workspace, then click Complete Scenario again.'
          : `Upload all required documents (${uploadedCount}/${totalRequired} so far) in the Workspace, then click Complete Scenario again.`
      );
    }
  };

  return (
    <div className="scenario-view">
      <div className="scenario-header-bar">
        <div className="scenario-info">
          <h1>{scenario.title}</h1>
        </div>
        
        <div className="scenario-header-actions">
          <div className="pill-buttons">
            <button
              className={`pill-button ${activeOverlay === 'context' ? 'active' : ''}`}
              onClick={() => setActiveOverlay(activeOverlay === 'context' ? null : 'context')}
            >
              <Info size={14} />
              <span>Context</span>
            </button>
            <button
              className={`pill-button ${activeOverlay === 'uploads' ? 'active' : ''}`}
              onClick={() => setActiveOverlay(activeOverlay === 'uploads' ? null : 'uploads')}
            >
              <Upload size={14} />
              <span>{uploadLabel}</span>
              {totalRequired > 0 && (
                <span className="pill-badge">{uploadedCount}/{totalRequired}</span>
              )}
            </button>
            <button
              className={`pill-button ${activeOverlay === 'tasks' ? 'active' : ''}`}
              onClick={() => setActiveOverlay(activeOverlay === 'tasks' ? null : 'tasks')}
            >
              <ListTodo size={14} />
              <span>Tasks</span>
            </button>
          </div>
          
          <div className="scenario-actions">
            <button
              className="action-button"
              onClick={() => { setUploadError(null); setWhiteboardOpen(true); }}
            >
              <FileText size={18} />
              Workspace
            </button>
            <button
              className="action-button"
              onClick={() => setMentorOpen(true)}
            >
              <MessageCircle size={18} />
              Product Sensei
            </button>
          </div>
        </div>
      </div>

      {/* Overlay for Context */}
      {activeOverlay === 'context' && (
        <div className="overlay-backdrop" onClick={() => setActiveOverlay(null)}>
          <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
            <div className="overlay-header">
              <h2>Context</h2>
              <button className="overlay-close" onClick={() => setActiveOverlay(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="overlay-body">
              <div className="context-container">
            {/* Quick Start Card */}
            {scenario.quickStart && (
              <div className="context-card quick-start">
                <div className="context-header">
                  <h3>Quick Start</h3>
                </div>
                <div className="context-content">
                  <p className="quick-start-text">{scenario.quickStart.firstStep}</p>
                  {scenario.timeline && (
                    <p className="timeline-text"><strong>Timeline:</strong> {scenario.timeline}</p>
                  )}
                  {scenario.quickStart.keyStakeholders && scenario.quickStart.keyStakeholders.length > 0 && (
                    <div className="key-stakeholders">
                      <p className="stakeholder-hint-label"><strong>Key stakeholders to talk to:</strong></p>
                      <ul className="stakeholder-list">
                        {scenario.quickStart.keyStakeholders.map((stakeholder, index) => (
                          <li key={index}>
                            <strong>{stakeholder.name}</strong> - {stakeholder.reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Product Brief (first scenario): editable gdoc → save copy, edit, PDF, upload */}
            {scenario.productBriefDoc && scenario.productBriefInstructions && (
              <div className="context-card product-brief-doc">
                <div className="context-header">
                  <h3>Product Brief (required to complete scenario)</h3>
                </div>
                <div className="context-content">
                  <p className="product-brief-summary">{scenario.productBriefInstructions.summary}</p>
                  <p className="product-brief-link-label"><strong>Unfinished Product Brief (Google Doc):</strong></p>
                  <a
                    href={scenario.productBriefDoc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="product-brief-link"
                  >
                    Open Product Brief template
                  </a>
                  <p className="product-brief-workflow-label"><strong>Workflow:</strong></p>
                  <ol className="product-brief-workflow">
                    {scenario.productBriefInstructions.workflow.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                  <p className="product-brief-define-label"><strong>What you define:</strong></p>
                  <p className="product-brief-define">{scenario.productBriefInstructions.whatYouDefine}</p>
                </div>
              </div>
            )}

            {/* Who Can Help Card */}
            {scenario.stakeholderKnowledge && Object.keys(scenario.stakeholderKnowledge).length > 0 && (
              <div className="context-card stakeholder-hints">
                <div className="context-header">
                  <h3>Who Can Help</h3>
                </div>
                <div className="context-content">
                  {Object.entries(scenario.stakeholderKnowledge).map(([stakeholderId, info]) => {
                    // Map stakeholder IDs to names
                    const stakeholderNames = {
                      'leadership-ceo': 'Jamie (CEO)',
                      'leadership-vp': 'Drew (VP Product)',
                      'customer-success': 'Blake (Customer Success)',
                      'data-analyst': 'Morgan (Data Analyst)',
                      'data-scientist': 'Taylor (Data Scientist)',
                      'engineering-backend': 'Alex (Backend Engineer)',
                      'engineering-frontend': 'Jordan (Frontend Engineer)',
                      'engineering-ml': 'Sam (ML Engineer)',
                      'design-ux': 'Riley (UX Designer)',
                      'design-ui': 'Casey (UI Designer)'
                    };
                    const stakeholderName = stakeholderNames[stakeholderId] || stakeholderId;
                    
                    return (
                      <div key={stakeholderId} className="stakeholder-hint-item">
                        <p className="stakeholder-name-hint">
                          <strong>Ask {stakeholderName}</strong> about:
                        </p>
                        <ul className="stakeholder-knows-list">
                          {info.knows.map((topic, idx) => (
                            <li key={idx}>{topic}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {scenario.company && (
              <div className="context-card company-context">
                <div className="context-header">
                  <h3>Company Context</h3>
                </div>
                <div className="context-content">
                  <div className="company-name">
                    <strong>{scenario.company.name}</strong>
                    <span className="company-type">{scenario.company.type}</span>
                    {scenario.company.stage && (
                      <span className="company-stage">{scenario.company.stage}</span>
                    )}
                  </div>
                  <p className="company-description">{scenario.company.context}</p>
                  {scenario.company.metrics && (
                    <div className="company-metrics">
                      <p><strong>Key Metrics:</strong></p>
                      <ul>
                        {scenario.company.metrics.users && <li>Active users: {scenario.company.metrics.users}</li>}
                        {scenario.company.metrics.revenue && <li>Revenue: {scenario.company.metrics.revenue}</li>}
                        {scenario.company.metrics.growth && <li>Growth: {scenario.company.metrics.growth}</li>}
                        {scenario.company.metrics.teamSize && <li>Team size: {scenario.company.metrics.teamSize}</li>}
                      </ul>
                    </div>
                  )}
                  {scenario.company.competitors && (
                    <div className="company-competitors">
                      <p><strong>Competitors:</strong> {scenario.company.competitors}</p>
                    </div>
                  )}
                  {scenario.company.recentEvents && (
                    <div className="company-events">
                      <p><strong>Recent Events:</strong> {scenario.company.recentEvents}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {scenario.currentSituation && (
              <div className="context-card situation-context">
                <div className="context-header">
                  <h3>Current Situation</h3>
                </div>
                <div className="context-content">
                  <p>{scenario.currentSituation}</p>
                  {scenario.constraints && (
                    <div className="scenario-constraints">
                      <p><strong>Constraints:</strong> {scenario.constraints}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overlay for Required Uploads */}
      {activeOverlay === 'uploads' && (
        <div className="overlay-backdrop" onClick={() => setActiveOverlay(null)}>
          <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
            <div className="overlay-header">
              <h2>{uploadLabel}</h2>
              <button className="overlay-close" onClick={() => setActiveOverlay(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="overlay-body">
              {uploadError && (
                <div className="upload-error-banner" role="alert">
                  {uploadError}
                  <button type="button" className="upload-error-dismiss" onClick={() => setUploadError(null)} aria-label="Dismiss">×</button>
                </div>
              )}
              <ArtifactRequirementsPanel
                tasks={scenario.tasks}
                artifacts={artifacts}
                scenarioId={scenarioId}
                onOpenWorkspace={() => {
                  setUploadError(null);
                  setWhiteboardOpen(true);
                  setActiveOverlay(null);
                }}
                inOverlay={true}
              />
              <div className="uploads-overlay-complete-section">
                {completeScenarioMessage && (
                  <p className="complete-scenario-message" role="alert">
                    {completeScenarioMessage}
                  </p>
                )}
                <button
                  type="button"
                  className={`action-button complete-scenario-btn ${canCompleteScenario ? 'can-complete' : ''}`}
                  onClick={handleCompleteScenarioClick}
                  title={canCompleteScenario ? 'Finish this scenario and see feedback' : `Upload required documents (${uploadedCount}/${totalRequired}) to complete`}
                >
                  <Award size={18} />
                  {canCompleteScenario ? 'Complete Scenario' : `Complete Scenario (${uploadedCount}/${totalRequired} uploads)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overlay for Tasks */}
      {activeOverlay === 'tasks' && (
        <div className="overlay-backdrop" onClick={() => setActiveOverlay(null)}>
          <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
            <div className="overlay-header">
              <h2>Tasks</h2>
              <button className="overlay-close" onClick={() => setActiveOverlay(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="overlay-body">
              <div className="tasks-container">
                <div className="tasks-list">
                  {scenario.tasks.map((task, index) => {
                    const isCompleted = completedTasks.includes(index);
                    return (
                      <div
                        key={index}
                        className={`task-item ${index === currentTask ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                        onClick={() => {
                          setCurrentTask(index);
                          setActiveOverlay(null);
                        }}
                      >
                        <div className="task-number">{index + 1}</div>
                        <div className="task-content">
                          <h4>{task.title}</h4>
                          <p>{task.description}</p>
                          {isCompleted && (
                            <span className="task-completed-badge">✓ Completed</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="scenario-content">
        <div className="scenario-main">
          <ChatInterface
            scenarioContext={scenarioContext}
            onMessageSent={(message) => {
              // Track user actions for scoring
              console.log('User action:', message);
            }}
            onSendToSensei={(selectedText, selectedMessage, context) => {
              // Format the prompt with context
              const companyName = context?.company?.name || scenario?.company?.name || 'the company';
              const stakeholderName = selectedMessage?.stakeholder?.name || 'a stakeholder';
              const stakeholderRole = selectedMessage?.stakeholder?.role || '';
              
              const formattedPrompt = `I'm a PM at ${companyName} doing a simulation and need guidance on how to respond to this message from ${stakeholderName}${stakeholderRole ? ` (${stakeholderRole})` : ''}:\n\n"${selectedText}"\n\nPlease provide guidance on:\n- Key points I should address in my response\n- Perspectives or considerations I should think about\n- Questions I should ask to clarify\n- Any frameworks or approaches that would be helpful\n\nI want guidance on HOW to respond, not a full response written for me. Act as a mentor giving me pointers and things to consider.`;
              
              setMentorInitialMessage(formattedPrompt);
              setMentorOpen(true);
            }}
          />
        </div>
      </div>

      <Whiteboard
        isOpen={whiteboardOpen}
        onClose={() => setWhiteboardOpen(false)}
        scenarioId={scenarioId}
        uploadError={uploadError}
        onArtifactsChange={(artifactsFromWorkspace) => {
          const list = (artifactsFromWorkspace || []).filter(a => a.scenarioId != null);
          setArtifacts(list);
        }}
        onSaveUpload={(payload) => {
          setUploadError(null);
          if (!scenarioId) {
            setUploadError('Scenario not set. Reload the page and try again.');
            return Promise.reject(new Error('No scenarioId'));
          }
          return saveScenarioUpload(scenarioId, payload)
            .then((res) => {
              setArtifacts(prev =>
                prev.some(
                  a => a.scenarioId === scenarioId && a.fileName === payload.fileName
                )
                  ? prev.map(a =>
                      a.scenarioId === scenarioId && a.fileName === payload.fileName
                        ? { ...a, id: res.id, uploadedAt: res.uploadedAt, fileSize: res.fileSize ?? a.fileSize }
                        : a
                    )
                  : [...prev, { id: res.id, scenarioId, fileName: res.fileName, taskIndex: res.taskIndex, fileSize: res.fileSize ?? payload.fileSize, uploadedAt: res.uploadedAt }]
              );
              return getAllUploads();
            })
            .then((uploads) => {
              const list = (uploads || []).map(u => ({
                id: u.id || `saved-${u.scenarioId || ''}-${u.fileName}-${u.uploadedAt}`,
                scenarioId: u.scenarioId ?? u.scenario_id ?? null,
                fileName: u.fileName || u.file_name,
                taskIndex: u.taskIndex ?? u.task_index ?? null,
                fileSize: u.fileSize ?? u.file_size ?? null,
                uploadedAt: u.uploadedAt || u.uploaded_at
              }));
              setArtifacts(list.filter(a => a.scenarioId));
            })
            .catch((err) => {
              const msg = err?.response?.data?.error || err?.message || 'Upload could not be saved';
              console.error('[ScenarioView] Save upload failed:', msg);
              setUploadError(msg);
            });
        }}
        savedArtifacts={artifacts}
        tasks={scenario?.tasks || []}
        scenarioTitles={Object.fromEntries(Object.entries(SCENARIO_DATA).map(([id, s]) => [id, s.title]))}
      />

      <AIMentor
        isOpen={mentorOpen}
        onClose={() => {
          setMentorOpen(false);
          // Reset after a delay to allow component to unmount
          setTimeout(() => {
            setMentorInitialMessage(null);
          }, 100);
        }}
        scenarioContext={scenarioContext}
        initialMessage={mentorInitialMessage}
      />

      <ScenarioNudges
        scenarioId={scenarioId}
        onComplete={() => {
          // Nudges completed
        }}
      />

      {scenarioComplete && (
        <ScenarioComplete
          scenario={scenario}
          decisions={{}}
          onRetry={() => {
            setScenarioComplete(false);
            setCurrentTask(0);
          }}
          userActions={[]}
          artifacts={artifacts.filter(a => a.scenarioId === scenarioId)}
        />
      )}
    </div>
  );
};

export default ScenarioView;



